import {useEffect, useRef, useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {Save, ShieldAlert} from "lucide-react";
import {
    COUNTRY_SCOPE_STORAGE_KEY,
    getAdminCountryScope,
    resolveAdminCountryScope,
} from "../../shared/api/countryScope";
import {getAuthToken} from "../../shared/api/client";
import {useCapabilities} from "../../shared/auth/CapabilitiesContext";
import {DataState} from "../../shared/components/DataState";
import {MutationResultBanner} from "../../shared/components/MutationResultBanner";
import {mapFinanceError} from "../../shared/finance/errors";
import {getActorCountryScopeBlock} from "../../shared/finance/actorCountryScope";
import {getRefundPolicy, refundKeys, updateRefundPolicy} from "./api";
import {useTranslation} from "../../shared/i18n";
import "./refunds.css";

const emptyPolicyForm = () => ({
    unlimited: true,
    refund_window_days: "",
    requires_approval: false,
    partial_refund_enabled: false,
    legacy_client_direct_enabled: false,
});

const formForPolicy = (policy) => ({
    unlimited: policy.refund_window_days === null,
    refund_window_days:
        policy.refund_window_days === null ? "" : String(policy.refund_window_days),
    requires_approval: policy.requires_approval,
    partial_refund_enabled: policy.partial_refund_enabled,
    legacy_client_direct_enabled: policy.legacy_client_direct_enabled,
});

export const RefundPolicyPage = () => {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const capabilities = useCapabilities();
    const [countryScope, setCountryScope] = useState(() =>
        resolveAdminCountryScope({
            role: capabilities.user?.role,
            allowedCountryIds: capabilities.scope?.country_ids || [],
            storedScope:
                localStorage.getItem(COUNTRY_SCOPE_STORAGE_KEY) ?? getAdminCountryScope(),
        }),
    );
    const activeCountryScopeRef = useRef(countryScope);
    const [form, setForm] = useState(emptyPolicyForm);
    const [validationError, setValidationError] = useState("");
    const [mutationResult, setMutationResult] = useState(null);
    const scopeBlock = getActorCountryScopeBlock({
        countryScope,
        actorCountryId: capabilities.country?.id,
    });
    const canManagePolicy = capabilities.hasAction("refunds.manage_policy");

    const policyQuery = useQuery({
        queryKey: refundKeys.policy(countryScope),
        queryFn: () => getRefundPolicy({
            countryScope,
            actorCountryId: capabilities.country?.id,
        }),
        enabled: countryScope !== null && scopeBlock === null,
        retry: false,
    });

    useEffect(() => {
        if (policyQuery.data) setForm(formForPolicy(policyQuery.data));
    }, [policyQuery.data]);

    useEffect(() => {
        const handleCountryChange = (event) => {
            const nextScope = event.detail?.countryId;
            const normalizedScope = nextScope == null ? null : String(nextScope);
            activeCountryScopeRef.current = normalizedScope;
            setCountryScope(normalizedScope);
            setForm(emptyPolicyForm());
            setValidationError("");
            setMutationResult(null);
        };
        window.addEventListener("sorpa-admin-country-scope-change", handleCountryChange);
        return () =>
            window.removeEventListener("sorpa-admin-country-scope-change", handleCountryChange);
    }, []);

    const policyMutation = useMutation({
        mutationFn: ({payload, requestScope}) =>
            updateRefundPolicy({
                countryScope: requestScope,
                actorCountryId: capabilities.country?.id,
                payload,
            }),
        onSuccess: (policy, variables) => {
            if (getAuthToken() !== variables.requestSession) return;
            queryClient.setQueryData(refundKeys.policy(variables.requestScope), policy);
            if (activeCountryScopeRef.current !== variables.requestScope) return;
            setForm(formForPolicy(policy));
            setValidationError("");
            setMutationResult({kind: "success", message: t("money.refunds.policy.saved")});
        },
        onError: (error, variables) => {
            if (
                getAuthToken() !== variables.requestSession ||
                activeCountryScopeRef.current !== variables.requestScope
            ) {
                return;
            }
            setMutationResult(mapFinanceError(error));
        },
    });

    const setField = (field, value) => {
        setForm((current) => ({...current, [field]: value}));
        setValidationError("");
        setMutationResult(null);
    };

    const submitPolicy = (event) => {
        event.preventDefault();
        if (!canManagePolicy || scopeBlock || countryScope === null) return;

        let refundWindowDays = null;
        if (!form.unlimited) {
            const daysText = form.refund_window_days.trim();
            if (!daysText) {
                setValidationError(t("money.refunds.policy.windowRequired"));
                return;
            }
            if (!/^\d+$/.test(daysText)) {
                setValidationError(t("money.refunds.policy.windowInteger"));
                return;
            }
            refundWindowDays = Number(daysText);
            if (!Number.isSafeInteger(refundWindowDays)) {
                setValidationError(t("money.refunds.policy.windowTooLarge"));
                return;
            }
        }

        setValidationError("");
        setMutationResult(null);
        policyMutation.mutate({
            payload: {
                refund_window_days: refundWindowDays,
                requires_approval: form.requires_approval,
                partial_refund_enabled: form.partial_refund_enabled,
                legacy_client_direct_enabled: form.legacy_client_direct_enabled,
            },
            requestScope: countryScope,
            requestSession: getAuthToken(),
        });
    };

    return (
        <div className="admin-page refund-policy-page">
            <header className="admin-page-header refund-policy-header">
                <div>
                    <h1 className="admin-page-title">{t("money.refunds.policy.title")}</h1>
                    <p className="admin-page-copy">
                        {t("money.refunds.policy.copy")}
                    </p>
                </div>
                {policyQuery.data ? (
                    <span className="refund-policy-version">{t("money.refunds.policy.version", {version: policyQuery.data.version})}</span>
                ) : null}
            </header>

            {scopeBlock ? (
                <>
                    <p className="refund-scope-warning">
                        <ShieldAlert aria-hidden="true" size={16} />
                        {scopeBlock.title}
                    </p>
                    <DataState
                        title={scopeBlock.title}
                        copy={scopeBlock.copy}
                    />
                </>
            ) : policyQuery.isLoading ? (
                <div className="admin-table-skeleton" aria-label={t("money.refunds.policy.loading")}>
                    <span /><span /><span />
                </div>
            ) : policyQuery.isError ? (
                <DataState
                    title={t("money.refunds.policy.loadError")}
                    copy={mapFinanceError(policyQuery.error).message}
                />
            ) : policyQuery.data ? (
                <>
                    <MutationResultBanner result={mutationResult} />
                    {validationError ? <p className="admin-form-error" role="alert">{validationError}</p> : null}
                    <form className="refund-policy-form" onSubmit={submitPolicy}>
                        <section className="refund-policy-section" aria-labelledby="refund-window-title">
                            <div>
                                <h2 id="refund-window-title">{t("money.refunds.policy.windowTitle")}</h2>
                                <p>{t("money.refunds.policy.windowNote")}</p>
                            </div>
                            <div className="refund-policy-window-controls">
                                <label className="admin-field">
                                    <span>{t("money.refunds.policy.windowDays")}</span>
                                    <input
                                        aria-label={t("money.refunds.policy.windowDays")}
                                        type="number"
                                        min="0"
                                        step="1"
                                        value={form.refund_window_days}
                                        disabled={!canManagePolicy || form.unlimited}
                                        onChange={(event) =>
                                            setField("refund_window_days", event.target.value)}
                                    />
                                </label>
                                <label className="refund-checkbox-control">
                                    <input
                                        type="checkbox"
                                        checked={form.unlimited}
                                        disabled={!canManagePolicy}
                                        onChange={(event) => setField("unlimited", event.target.checked)}
                                    />
                                    <span>{t("money.refunds.policy.unlimited")}</span>
                                </label>
                            </div>
                        </section>

                        <section className="refund-policy-section" aria-labelledby="refund-rules-title">
                            <div>
                                <h2 id="refund-rules-title">{t("money.refunds.policy.rulesTitle")}</h2>
                                <p>{t("money.refunds.policy.rulesNote")}</p>
                            </div>
                            <div className="refund-policy-rules">
                                <label className="refund-checkbox-control">
                                    <input
                                        type="checkbox"
                                        checked={form.requires_approval}
                                        disabled={!canManagePolicy}
                                        onChange={(event) =>
                                            setField("requires_approval", event.target.checked)}
                                    />
                                    <span>{t("money.refunds.policy.requireApproval")}</span>
                                </label>
                                <label className="refund-checkbox-control">
                                    <input
                                        type="checkbox"
                                        checked={form.partial_refund_enabled}
                                        disabled={!canManagePolicy}
                                        onChange={(event) =>
                                            setField("partial_refund_enabled", event.target.checked)}
                                    />
                                    <span>{t("money.refunds.policy.allowPartial")}</span>
                                </label>
                                <label className="refund-checkbox-control">
                                    <input
                                        type="checkbox"
                                        checked={form.legacy_client_direct_enabled}
                                        disabled={!canManagePolicy}
                                        onChange={(event) =>
                                            setField("legacy_client_direct_enabled", event.target.checked)}
                                    />
                                    <span>{t("money.refunds.policy.allowLegacy")}</span>
                                </label>
                            </div>
                        </section>

                        {canManagePolicy ? (
                            <div className="refund-policy-actions">
                                <button
                                    className="admin-button admin-button-primary"
                                    type="submit"
                                    disabled={policyMutation.isPending}
                                >
                                    <Save aria-hidden="true" size={16} />
                                    {policyMutation.isPending ? t("money.refunds.policy.saving") : t("money.refunds.policy.save")}
                                </button>
                            </div>
                        ) : null}
                    </form>
                </>
            ) : null}
        </div>
    );
};
